import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Academy CMS SSO: opened as `/#/sso?code=...`. Exchanges code for JWT on the academy API.
 * Set REACT_APP_ACADEMY_API_BASE to the academy backend docroot (e.g. https://api.smdeducationacademy.com/public).
 */
function academyApiRoot() {
  const raw = process.env.REACT_APP_ACADEMY_API_BASE;
  if (raw != null && String(raw).trim() !== '') {
    return String(raw).trim().replace(/\/$/, '');
  }
  return 'https://api.smdeducationacademy.com/public';
}

const SsoCallback = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const navigate = useNavigate();
  const { applyAcademySsoSession } = useAuth();
  const [message, setMessage] = useState('Signing you in…');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!code) {
        navigate('/login?sso=missing', { replace: true });
        return;
      }

      try {
        const base = academyApiRoot();
        const res = await fetch(`${base}/api/admin/sso-exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ code }),
        });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;

        const data = body?.data;
        if (!res.ok || body?.status !== 'success' || !data?.token) {
          throw new Error(body?.message || 'SSO exchange failed');
        }

        applyAcademySsoSession({
          token: data.token,
          username: data.username,
          adminId: data.admin_id,
        });
        navigate('/dashboard', { replace: true });
      } catch (e) {
        if (cancelled) return;
        setMessage(e?.message || 'SSO failed');
        navigate('/login?sso=error', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, navigate, applyAcademySsoSession]);

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center gap-4 text-slate-300">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      <p className="text-sm">{message}</p>
    </div>
  );
};

export default SsoCallback;
